import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { StorageFunctions } from '@generals';
import { ConsultaPadrao, DimDatatableColumn } from '@models';
import { MessageService } from '@services';
import { DocumentosAnexosServiceV2 } from '@sv2/documentos-anexos/documentos-anexos-v2.service';
import { getErrorMessage } from '@utils';
import * as saveAs from 'file-saver';
import { DocumentosAnexosForm, DocumentosAnexosViewModel } from 'models/documentos-anexos/DocumentosAnexosViewModel';

@Component({
  selector: 'app-consulta-documentos-anexos',
  templateUrl: './consulta-documentos-anexos.component.html',
  styleUrls: ['./consulta-documentos-anexos.component.scss']
})
export class ConsultaDocumentosAnexosComponent extends ConsultaPadrao<DocumentosAnexosViewModel> implements OnInit, OnChanges {

  @Output() onChangedDocumentosAnexos = new EventEmitter<DocumentosAnexosViewModel[]>();

  @Input() despesaID: number = null;
  @Input() pedidoItemID: number = null;
  @Input() botaoEnviarArquivoVisivel = true;

  extensoesInputAccept = [".jpg", ".jpeg", ".png", ".pdf", ".txt", ".xlsx", ".xls", ".xml", ".doc", ".docx", ".rtf", ".odt", ".csv", ".html", ".htm"];
  documentosAnexosSalvos: DocumentosAnexosViewModel[] = [];
  documentosAnexosParaUpload: File[] = [];
  colunasDocumentosAnexos: DimDatatableColumn[] = [
    { label: 'Documento', col: 'nomeArquivo', hide: true },
    { label: 'Origem', col: 'origem', hide: true },
  ];

  constructor(
    message: MessageService,
    storage: StorageFunctions,
    private service: DocumentosAnexosServiceV2,
  ) {
    super('ConsultaDocumentosAnexosComponent', message, storage);
  }

  get listaAnexos() {
    return this.documentosAnexosParaUpload.map(doc => { return { nomeArquivo: doc.name + ' (Salvar pendente)', origem: 'T' } as DocumentosAnexosViewModel }).concat(this.documentosAnexosSalvos);
  }

  get anexosLoading() {
    return this.loading['enviarDocumentosAnexos'] || this.loading['downloadDocumentoAnexo'] || this.loading['excluirDocumentoAnexo'] || this.loading['searchDocumentoAnexo'];
  }

  get pesquisaValida() {
    return this.form.get('pedidoItemID').value != null || this.form.get('despesaID').value != null || this.form.get('tituloCobrancaID').value != null || this.form.get('tituloFornecedorID').value != null || this.form.get('ordemCompraItemID').value != null;
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['despesaID'] || changes['pedidoItemID']) {
      this.handleChanges();
    }
  }

  async handleChanges() {
    this.form = this.getFormDefault();
    await this.enviarDocumentosAnexos();
    await this.search();
  }

  getFormDefault(): FormGroup<DocumentosAnexosForm> {
    const despesaID = isNaN(Number(this.despesaID)) ? null : Number(this.despesaID);

    const f = new FormGroup({
      tituloCobrancaID: new FormControl(null),
      tituloFornecedorID: new FormControl(null),
      pedidoItemID: new FormControl(this.pedidoItemID),
      ordemCompraItemID: new FormControl(null),
      despesaID: new FormControl(despesaID),
      id: new FormControl(null),
      unidadeEmpresaID: new FormControl(null),
      nomeArquivo: new FormControl(null),
      localizacao: new FormControl(null),
      origem: new FormControl(null),
      paramConsulta: new FormGroup({
        index: new FormControl(1),
        order: new FormGroup({
          dir: new FormControl('asc'),
          prop: new FormControl('id'),
        }),
      }),
    });

    return f;
  }

  async handleDownloadDocumentoAnexo(vm: DocumentosAnexosViewModel) {
    await this.downloadDocumentoAnexo(vm);
  }

  async downloadDocumentoAnexo(documento: DocumentosAnexosViewModel) {
    if (!documento || !documento.id) {
      return;
    }

    try {
      this.loading['downloadDocumentoAnexo'] = true;
      const anexoBlob = await this.service.downloadDocumentoAnexo(documento.id);
      saveAs(anexoBlob, documento.nomeArquivo);
    } catch (e) {
      console.error(e);
      this.message.error('Falha ao baixar anexo.', 0, 'Erro');
    } finally {
      this.loading['downloadDocumentoAnexo'] = false;
    }
  }

  async handleOnExcluirDocumentoAnexo(documento: DocumentosAnexosViewModel) {
    await this.excluirDocumentoAnexo(documento);
    await this.search();
  }

  handleOnChangeUploadDocumentosAnexos(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files as FileList;
    if (!files.length) {
      return;
    }

    this.adicionarDocumentosAnexos(files);
  }

  adicionarDocumentosAnexos(files: FileList) {
    if (this.documentosAnexosParaUpload.length >= 50) {
      this.message.info('Quantidade máxima de arquivos atingida.', 5000, 'Muitos arquivos');
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (file.size > 10485760) {
        this.message.info('O arquivo selecionado é muito grande. Tamanho máximo por arquivo: 10MB.', 5000, 'Arquivo muito grande');
        continue;
      } else if (this.documentosAnexosParaUpload.find(doc => doc.name == file.name)) {
        this.message.info('Um arquivo com este nome já existe.', 5000, 'Arquivo duplicado');
        continue;
      }

      this.documentosAnexosParaUpload.push(file);
    }
  }

  async excluirDocumentoAnexo(documento: DocumentosAnexosViewModel) {
    if (!documento || !documento.id) {
      return;
    }

    try {
      this.loading['excluirDocumentoAnexo'] = true;
      await this.service.deleteAsPromise(documento.id);
    } catch (e) {
      console.error(e);
      this.message.error('Falha ao remover anexo.', 0, 'Erro');
    } finally {
      this.loading['excluirDocumentoAnexo'] = false;
    }
  }

  async search(): Promise<void> {
    if (this.form.invalid || !this.pesquisaValida) {
      return;
    }

    try {
      this.loading['searchDocumentoAnexo'] = true;
      const response = await this.service.consultarModelosColecao(this.form.getRawValue());
      this.searchData = response;
      this.documentosAnexosSalvos = this.searchData.items;
    } catch (error) {
      console.error(error);
      this.message.error(getErrorMessage(error));
    } finally {
      this.loading['searchDocumentoAnexo'] = false;
    }
  }

  async enviarDocumentosAnexos() {
    if (this.documentosAnexosParaUpload.length < 1 || !this.pesquisaValida) {
      return;
    }

    try {
      this.loading['enviarDocumentosAnexos'] = true;
      await this.service.uploadDocumentosAnexos(this.form.getRawValue(), this.documentosAnexosParaUpload);
      this.documentosAnexosParaUpload.length = 0;
    } catch (e) {
      console.error(e);
      this.message.error('Falha ao enviar os anexos.', 7500, 'Erro', false);
    } finally {
      this.loading['enviarDocumentosAnexos'] = false;
    }
  }

  get possuiDocumentosAnexosPendentes(): boolean {
    return this.documentosAnexosParaUpload.length > 0;
  }

  limparDocumentosAnexosParaUpload() {
    this.documentosAnexosParaUpload.length = 0;
  }
}
